import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, X, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import CarCard from "../components/CarCard";
import { api, BRANDS, FUEL_TYPES, TRANSMISSIONS, CITIES, INR } from "../lib/gj";

const ANY = "any";

function FiltersPanel({ filters, setFilters, onApply, onClear }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">Brand</label>
        <Select
          value={filters.brand || ANY}
          onValueChange={(v) => setFilters((f) => ({ ...f, brand: v === ANY ? "" : v }))}
        >
          <SelectTrigger className="mt-2 bg-white" data-testid="filter-brand">
            <SelectValue placeholder="Any brand" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any brand</SelectItem>
            {BRANDS.map((b) => (
              <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">Fuel Type</label>
        <Select
          value={filters.fuel_type || ANY}
          onValueChange={(v) => setFilters((f) => ({ ...f, fuel_type: v === ANY ? "" : v }))}
        >
          <SelectTrigger className="mt-2 bg-white" data-testid="filter-fuel">
            <SelectValue placeholder="Any fuel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any fuel</SelectItem>
            {FUEL_TYPES.map((f) => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">Transmission</label>
        <Select
          value={filters.transmission || ANY}
          onValueChange={(v) => setFilters((f) => ({ ...f, transmission: v === ANY ? "" : v }))}
        >
          <SelectTrigger className="mt-2 bg-white" data-testid="filter-transmission">
            <SelectValue placeholder="Any transmission" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any transmission</SelectItem>
            {TRANSMISSIONS.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">Location</label>
        <Select
          value={filters.location || ANY}
          onValueChange={(v) => setFilters((f) => ({ ...f, location: v === ANY ? "" : v }))}
        >
          <SelectTrigger className="mt-2 bg-white" data-testid="filter-location">
            <SelectValue placeholder="Any city" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any city</SelectItem>
            {CITIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">Budget</label>
          <div className="text-xs text-[#0A192F] font-semibold">
            {INR(filters.budget[0])} – {INR(filters.budget[1])}
          </div>
        </div>
        <div className="mt-4 px-1">
          <Slider
            value={filters.budget}
            min={50000}
            max={3000000}
            step={25000}
            onValueChange={(v) => setFilters((f) => ({ ...f, budget: v }))}
            data-testid="filter-budget"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          onClick={onApply}
          className="flex-1 bg-[#0A192F] hover:bg-[#112240] text-white"
          data-testid="filter-apply-btn"
        >
          Apply filters
        </Button>
        <Button
          onClick={onClear}
          variant="outline"
          className="border-[#0A192F]/20 text-[#0A192F]"
          data-testid="filter-clear-btn"
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

export default function Cars() {
  const [params, setParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [filters, setFilters] = useState({
    brand: params.get("brand") || "",
    fuel_type: params.get("fuel_type") || "",
    transmission: params.get("transmission") || "",
    location: params.get("location") || "",
    budget: [
      parseInt(params.get("min_price") || "50000", 10),
      parseInt(params.get("max_price") || "3000000", 10),
    ],
  });

  const apiParams = useMemo(() => {
    const p = { status: "approved" };
    if (q) p.q = q;
    if (filters.brand) p.brand = filters.brand;
    if (filters.fuel_type) p.fuel_type = filters.fuel_type;
    if (filters.transmission) p.transmission = filters.transmission;
    if (filters.location) p.location = filters.location;
    p.min_price = filters.budget[0];
    p.max_price = filters.budget[1];
    return p;
  }, [q, filters]);

  const fetchCars = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cars", { params: apiParams });
      setCars(data);
    } catch (err) {
      console.warn("[cars] load failed:", err);
      setCars([]);
    } finally {
      setLoading(false);
    }
  }, [apiParams]);

  useEffect(() => {
    fetchCars();
  }, [fetchCars]);

  const onApply = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (filters.brand) next.set("brand", filters.brand);
    if (filters.fuel_type) next.set("fuel_type", filters.fuel_type);
    if (filters.transmission) next.set("transmission", filters.transmission);
    if (filters.location) next.set("location", filters.location);
    next.set("min_price", String(filters.budget[0]));
    next.set("max_price", String(filters.budget[1]));
    setParams(next);
    // fetchCars will re-run via memo + effect
  };

  const onClear = () => {
    setQ("");
    setFilters({
      brand: "",
      fuel_type: "",
      transmission: "",
      location: "",
      budget: [50000, 3000000],
    });
    setParams(new URLSearchParams());
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="cars-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="gj-divider-gold mb-3" />
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A192F]">
            All Cars
          </h1>
          <p className="text-[#4A5568] mt-1 text-sm">
            {loading ? "Loading…" : `${cars.length} cars available`}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onApply();
          }}
          className="bg-white rounded-lg border border-[#0A192F]/10 p-1 flex items-center gap-1 w-full sm:w-auto sm:min-w-[360px]"
        >
          <Search className="w-4 h-4 text-[#0A192F]/60 ml-2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search brand, model…"
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            data-testid="cars-search-input"
          />
          <Button
            type="submit"
            size="sm"
            className="bg-[#0A192F] hover:bg-[#112240] text-white"
            data-testid="cars-search-btn"
          >
            Search
          </Button>
        </form>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 bg-white border border-[#0A192F]/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="font-heading font-bold text-[#0A192F]">Filters</div>
              <Filter className="w-4 h-4 text-[#0A192F]/60" />
            </div>
            <FiltersPanel filters={filters} setFilters={setFilters} onApply={onApply} onClear={onClear} />
          </div>
        </aside>

        <div>
          <div className="lg:hidden mb-4">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full border-[#0A192F]/20 text-[#0A192F]" data-testid="mobile-filters-btn">
                  <Filter className="w-4 h-4 mr-2" /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="bg-white h-[85vh] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="font-heading">Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FiltersPanel
                    filters={filters}
                    setFilters={setFilters}
                    onApply={onApply}
                    onClear={onClear}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`car-skeleton-${i}`} className="bg-white rounded-xl border border-[#0A192F]/10 h-80 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && cars.length === 0 && (
            <div className="bg-white border border-[#0A192F]/10 rounded-xl p-12 text-center" data-testid="empty-cars-state">
              <div className="gj-divider-gold mx-auto mb-4" />
              <div className="font-heading text-lg font-bold text-[#0A192F]">
                No cars match your filters
              </div>
              <p className="text-sm text-[#4A5568] mt-2 max-w-md mx-auto leading-relaxed">
                Try adjusting your filters or reset to see all available
                inventory from our verified West Bengal dealers.
              </p>
              <Button onClick={onClear} className="mt-6 bg-[#0A192F] text-white">
                Reset filters
              </Button>
            </div>
          )}
          {!loading && cars.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((c) => (
                <CarCard key={c.id} car={c} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
